import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import LoadingState from './common/LoadingState';
import ErrorStateView from './common/ErrorState';

export interface LabTestTypeBreakdownRow {
    testType: string;
    count: number;
}

export interface LabConfirmationCoverageData {
    closedVisits: number;
    visitsWithField: number;
    labPerformedVisits: number;
    patientsWithLab: number;
    lesionsOnLabVisits: number;
    testTypeBreakdown: LabTestTypeBreakdownRow[];
}

export interface LabConfirmationCoveragePureProps {
    loading: boolean;
    error?: Error | null;
    data: LabConfirmationCoverageData | null;
    onRetry?: () => void;
}

const EMPTY_DATA: LabConfirmationCoverageData = {
    closedVisits: 0,
    visitsWithField: 0,
    labPerformedVisits: 0,
    patientsWithLab: 0,
    lesionsOnLabVisits: 0,
    testTypeBreakdown: [],
};

const coveragePercent = (numerator: number, denominator: number): number =>
    denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;

const KNOWN_LAB_TEST_TYPES = new Set(['smear', 'pcr', 'biopsy', 'culture', 'serology', 'other']);

const testTypeLabelId = (testType: string): string =>
    `hospital.analytics.clinical.lab-confirmation.test-type.${testType}`;

export const LabConfirmationCoveragePure: React.FC<LabConfirmationCoveragePureProps> = ({
    loading,
    error,
    data,
    onRetry,
}) => {
    const headCells = useMemo(() => ([
        { id: 'testType', alignment: 'left', label: <Translate id="hospital.analytics.clinical.lab-confirmation.test-type-column" /> },
        { id: 'count', alignment: 'right', label: <Translate id="hospital.analytics.clinical.lab-confirmation.count-column" /> },
    ]), []);

    if (loading) return <LoadingState />;
    if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

    const stats = data ?? EMPTY_DATA;
    const isEmpty = stats.closedVisits === 0;

    if (isEmpty) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    <Translate id="hospital.analytics.clinical.lab-confirmation.empty" />
                </Typography>
            </Box>
        );
    }

    const percent = coveragePercent(stats.labPerformedVisits, stats.closedVisits);
    const zeroLabRate = stats.closedVisits > 0 && stats.labPerformedVisits === 0;

    const tableRows = stats.testTypeBreakdown.map(row => ({
        id: row.testType,
        testType: KNOWN_LAB_TEST_TYPES.has(row.testType)
            ? <Translate id={testTypeLabelId(row.testType)} />
            : row.testType,
        count: row.count,
    }));

    return (
        <Box>
            <Typography variant="h5" component="p" sx={{ mb: 1 }}>
                <Translate
                    id="hospital.analytics.clinical.lab-confirmation.rate"
                    data={{
                        percent,
                        performed: stats.labPerformedVisits,
                        closed: stats.closedVisits,
                    }}
                />
            </Typography>
            {zeroLabRate && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    <Translate id="hospital.analytics.clinical.lab-confirmation.zero-rate" />
                </Typography>
            )}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="textSecondary">
                        <Translate id="hospital.analytics.clinical.lab-confirmation.patients" />: {stats.patientsWithLab}
                    </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                    <Typography variant="body2" color="textSecondary">
                        <Translate id="hospital.analytics.clinical.lab-confirmation.lesions" />: {stats.lesionsOnLabVisits}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                        <Translate
                            id="hospital.analytics.clinical.lab-confirmation.field-coverage"
                            data={{
                                filled: stats.visitsWithField,
                                closed: stats.closedVisits,
                            }}
                        />
                    </Typography>
                </Grid>
            </Grid>
            {tableRows.length > 0 ? (
                <EnhancedTable noHeader noSelectable headCells={headCells} rows={tableRows} />
            ) : (
                <Typography variant="body2" color="textSecondary">
                    <Translate id="hospital.analytics.clinical.lab-confirmation.no-types" />
                </Typography>
            )}
        </Box>
    );
};

const LabConfirmationCoverage = LabConfirmationCoveragePure;
export default LabConfirmationCoverage;
